import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCreateTeam, useTeams, useUpdateTeam, useUsers } from '../api/hooks';
import type { Team } from '../api/types';
import { TeamRoster } from '../components/TeamRoster';
import { EmptyState, ErrorBanner, Modal, TableSkeleton } from '../components/ui';

/**
 * The administrator's view of the org chart: which teams exist, who runs each
 * one, and who sits under them.
 *
 * A team is not just a label here — every active team's manager is in the
 * round-robin that new leads land on, so creating one or changing its manager
 * changes where leads go. The table says so rather than leaving it to be
 * discovered.
 */
export function TeamsPage() {
  const { data: teams = [], isPending, error } = useTeams();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Team | null>(null);
  const [rosterFor, setRosterFor] = useState<Team | null>(null);

  const active = teams.filter((t) => t.is_active);
  const withoutManager = active.filter((t) => !t.manager).length;

  // The roster modal holds a snapshot; re-read from the list so member counts
  // stay live while it is open.
  const rosterTeam = rosterFor ? (teams.find((t) => t.id === rosterFor.id) ?? rosterFor) : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Teams</h1>
          <p className="sub">
            {active.length} active {active.length === 1 ? 'team' : 'teams'} · each team's manager
            takes a turn in the new-lead rotation, then hands leads to their team specialists
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Add team</button>
        </div>
      </div>

      <ErrorBanner error={error} />

      {withoutManager > 0 && (
        <div className="banner banner-info">
          <div>
            {withoutManager} active {withoutManager === 1 ? 'team has' : 'teams have'} no manager.
            Those teams are skipped by the new-lead rotation entirely — set a manager, or deactivate
            the team.
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Team</th>
              <th>Manager</th>
              <th className="num">Team Specialists</th>
              <th>New leads go to</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          {isPending ? (
            <TableSkeleton cols={6} />
          ) : (
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} style={team.is_active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{team.name}</strong>
                    {team.description && (
                      <div className="hint" style={{ marginTop: 2 }}>{team.description}</div>
                    )}
                  </td>
                  <td>
                    {team.manager?.full_name ?? (
                      <span style={{ color: 'var(--danger)' }}>Not set</span>
                    )}
                  </td>
                  <td className="num">{team.member_count}</td>
                  <td>
                    {!team.manager ? (
                      <span style={{ color: 'var(--faint)' }}>—</span>
                    ) : !team.auto_distribute ? (
                      <span className="chip chip-plain">The manager, to hand out</span>
                    ) : team.auto_distribute_time ? (
                      <span className="chip" style={{ color: 'var(--good)', background: 'var(--good-bg)' }}>
                        The team, daily at {team.auto_distribute_time}
                      </span>
                    ) : (
                      <span className="chip" style={{ color: 'var(--good)', background: 'var(--good-bg)' }}>
                        The team, instantly
                      </span>
                    )}
                  </td>
                  <td>
                    {team.is_active ? (
                      <span className="chip" style={{ color: 'var(--good)', background: 'var(--sunken)' }}>
                        <span className="dot" />
                        Active
                      </span>
                    ) : (
                      <span className="chip chip-dup">
                        <span className="dot" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setRosterFor(team)}>
                      Team Specialists
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(team)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!isPending && teams.length === 0 && (
          <EmptyState
            title="No teams yet"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                Create the first team
              </button>
            }
          >
            A team is a manager plus the team specialists under them. Until one exists, new leads have
            nobody to route to.
          </EmptyState>
        )}
      </div>

      {creating && <TeamFormModal onClose={() => setCreating(false)} />}
      {editing && <TeamFormModal team={editing} onClose={() => setEditing(null)} />}
      {rosterTeam && (
        <Modal title={rosterTeam.name} onClose={() => setRosterFor(null)} width={760}
          footer={<button className="btn btn-primary" onClick={() => setRosterFor(null)}>Done</button>}
        >
          <TeamRoster team={rosterTeam} />
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TeamFormModal({ team, onClose }: { team?: Team; onClose: () => void }) {
  const isEdit = Boolean(team);
  const { data: users = [] } = useUsers(false);
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();

  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [managerId, setManagerId] = useState(team?.manager?.id ?? '');
  const [isActive, setIsActive] = useState(team?.is_active ?? true);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const busy = createTeam.isPending || updateTeam.isPending;

  // Only people who can actually run a team. A counsellor-scoped role sees
  // only its own leads, so a counsellor named as manager could not see — let
  // alone hand out — the leads routed to them; the API rejects it too.
  // Administrators are listed separately: legitimate in a small org, but not
  // the normal answer, so they don't sit at the top of the list.
  const managers = users.filter((u) => u.data_scope === 'team');
  const admins = users.filter((u) => u.data_scope === 'all');
  const manager = users.find((u) => u.id === managerId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      manager_user_id: managerId || null,
    };
    try {
      if (isEdit && team) {
        await updateTeam.mutateAsync({ id: team.id, ...body, is_active: isActive });
      } else {
        await createTeam.mutateAsync(body);
      }
      onClose();
    } catch (err) {
      setError(err);
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
    }
  }

  return (
    <Modal
      title={isEdit ? `Edit ${team!.name}` : 'Add team'}
      onClose={onClose}
      width={560}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="team-form" disabled={busy || !name.trim()}>
            {busy ? <span className="spinner" /> : isEdit ? 'Save changes' : 'Create team'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="team-form" onSubmit={submit}>
        <div className={`field${fieldErrors.name ? ' invalid' : ''}`}>
          <label htmlFor="t-name">Team name<span className="req">*</span></label>
          <input id="t-name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          {fieldErrors.name && <div className="err">{fieldErrors.name}</div>}
        </div>

        <div className="field">
          <label htmlFor="t-desc">Description</label>
          <input
            id="t-desc"
            placeholder="What this team covers"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="t-manager">Manager</label>
          <select id="t-manager" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">No manager</option>
            {managers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
                {u.role ? ` — ${u.role.name}` : ''}
              </option>
            ))}
            {admins.length > 0 && (
              <optgroup label="Administrators">
                {admins.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                    {u.role ? ` — ${u.role.name}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {managers.length === 0 && admins.length === 0 ? (
            <div className="hint" style={{ color: 'var(--warn)' }}>
              Nobody holds a manager role yet. Give someone the Manager role from Users first —
              team specialists can't run a team, because their role only shows them their own leads.
            </div>
          ) : !managerId ? (
            <div className="hint" style={{ color: 'var(--warn)' }}>
              Without a manager this team takes no part in the new-lead rotation.
            </div>
          ) : (
            <div className="hint">
              {manager?.data_scope === 'all'
                ? `${manager.full_name} is an administrator — they can run a team, but they already see every lead, so the team view adds nothing for them. `
                : ''}
              They take a turn in the new-lead rotation, and control how those leads reach the team
              from their own “My team” page. They are not on the roster themselves.
            </div>
          )}
        </div>

        {isEdit && (
          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
            <div className="hint">
              Deactivating takes this team's manager out of the new-lead rotation. Members and
              existing leads are left as they are.
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
